import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useSortableTable from '../common/useSortableTable';
import ShippingLinePayablesForm from './ShippingLinePayablesForm';
import OtherPayablesForm from './OtherPayablesForm';
import { openFilePreview } from '../common/FilePreviewModal';
import ConfirmDialog from '../common/ConfirmDialog';

const API_PAYABLES_BY_IDF = '/api/admin/payables-by-idf/';
const API_SHIPPING_LINE_PAYABLE = '/api/admin/shipping-line-payables/';
const API_OTHER_PAYABLE = '/api/admin/other-payables/';
const API_INVOICE_PREVIEW = '/api/documentation/jobs/invoice-preview/';

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json'
});

const STATUS_ORDER = {
    'APPROVED 2': 1,
    'APPROVED 1': 2,
    'PENDING': 3,
    'REJECTED': 4,
    'CLIENT PAID': 5
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
};

// Fetch a file (auth-safe), then open it in the FilePreviewModal popup as a PDF.
// Calls onFail when the file can't be loaded or the preview can't open.
const previewInvoiceFile = async (url, name, onFail) => {
    try {
        const res = await fetch(url, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load invoice');
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const fileName = /\.[A-Za-z0-9]+$/.test(name || '') ? name : `${name}.pdf`;
        const previewWindow = openFilePreview(blobUrl, fileName);
        if (!previewWindow) {
            URL.revokeObjectURL(blobUrl);
            if (onFail) onFail();
            return;
        }
        const revoke = () => URL.revokeObjectURL(blobUrl);
        previewWindow.addEventListener('beforeunload', revoke);
        setTimeout(revoke, 60000);
    } catch (err) {
        if (onFail) onFail();
    }
};

const formatDateDisplay = (value) => {
    if (!value) return '-';
    const str = String(value);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return str;
    return dt.toLocaleDateString('en-GB');
};

const getPayableDisplayAmount = (p, includeBankCharges = false) => {
    const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;
    const hasOldItems = p.items && p.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && p.items.some(item => !item.total_amount);
    const amount = parseFloat(p.total_amount || 0);
    let displayAmount = invoiceCurrencyDifferent && hasOldItems && p.usd_rate
        ? amount * parseFloat(p.usd_rate)
        : amount;
    if (includeBankCharges) {
        displayAmount += parseFloat(p.tiss_tips_amount || 0) + parseFloat(p.gepg_amount || 0);
    }
    return displayAmount;
};

const getChargeDisplayAmount = (item, p) => {
    const invoiceCurrencyDifferent = p.currency === 'TZS' && parseFloat(p.usd_rate || 0) > 100;
    const rawFx = parseFloat(p.usd_rate || 1);
    const fxRate = invoiceCurrencyDifferent
        ? (p.fx_round_off ? Math.round(rawFx) : rawFx)
        : 1;
    const amount = parseFloat(item.amount || 0);
    const foreignAmount = parseFloat(item.foreign_amount || 0);
    const totalAmount = parseFloat(item.total_amount || 0);
    const isAdjustment = item.charge_name === 'ADJUSTMENT';

    const effectiveAmount = (invoiceCurrencyDifferent && !isAdjustment && !foreignAmount)
        ? amount * fxRate
        : amount;

    const adjustmentTZS = isAdjustment
        ? (totalAmount || (invoiceCurrencyDifferent ? amount * fxRate : amount))
        : 0;

    return isAdjustment
        ? adjustmentTZS
        : (totalAmount > 0 ? totalAmount : (item.add_vat ? (effectiveAmount * 1.18) : effectiveAmount));
};

const PayableExpensesView = ({ idfNo, jobId, fromInvoicing, includeInvoiceCharges, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [payables, setPayables] = useState([]);
    const [invoiceCharges, setInvoiceCharges] = useState([]);
    const [viewPayable, setViewPayable] = useState(null);
    const [viewPayableType, setViewPayableType] = useState(null);
    const [selectedExpenseIds, setSelectedExpenseIds] = useState(new Set());
    const [fileNotAvailable, setFileNotAvailable] = useState(false);

    useEffect(() => {
        const fetchPayables = async () => {
            setLoading(true);
            setError(null);
            try {
                let url = `${API_PAYABLES_BY_IDF}?idf_no=${encodeURIComponent(idfNo)}`;
                if (jobId) {
                    url += `&job_id=${encodeURIComponent(jobId)}`;
                }
                if (fromInvoicing || includeInvoiceCharges) {
                    url += `&include_invoice_charges=1`;
                }
                const res = await fetch(url, { headers: getAuthHeaders() });
                if (!res.ok) throw new Error('Failed to fetch payables');
                const data = await res.json();
                if (Array.isArray(data)) {
                    setPayables(data);
                    setInvoiceCharges([]);
                } else {
                    setPayables(data.payables || []);
                    setInvoiceCharges(data.invoice_charges || []);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchPayables();
    }, [idfNo, jobId, fromInvoicing, includeInvoiceCharges]);

    const expenses = useMemo(() => {
        return payables
            .filter(p => p.status === 'APPROVED 3' || p.status === 'CLIENT PAID')
            .sort((a, b) => {
                const dateA = a.payment_date ? new Date(a.payment_date).getTime() : 0;
                const dateB = b.payment_date ? new Date(b.payment_date).getTime() : 0;
                return dateA - dateB;
            });
    }, [payables]);

    const allExpenses = useMemo(() => {
        return [...expenses, ...invoiceCharges];
    }, [expenses, invoiceCharges]);

    const pendingApproval = useMemo(() => {
        return payables
            .filter(p => p.status !== 'APPROVED 3' && p.status !== 'CLIENT PAID')
            .sort((a, b) => {
                const orderA = STATUS_ORDER[a.status] ?? 99;
                const orderB = STATUS_ORDER[b.status] ?? 99;
                return orderA - orderB;
            });
    }, [payables]);

    const bankTotals = useMemo(() => {
        const totals = {};
        allExpenses.forEach(p => {
            const key = `${p.mode}::${p.currency}`;
            if (!totals[key]) totals[key] = { mode: p.mode, currency: p.currency, total: 0 };
            totals[key].total += getPayableDisplayAmount(p, fromInvoicing);
        });
        return Object.values(totals).sort((a, b) => a.mode.localeCompare(b.mode));
    }, [allExpenses, fromInvoicing]);

    const uniqueJobIds = useMemo(() => {
        return [...new Set(payables.map(p => p.job_number).filter(Boolean))].join(', ') || '-';
    }, [payables]);

    const uniqueBlNumbers = useMemo(() => {
        return [...new Set(payables.map(p => p.bl_number).filter(Boolean))].join(', ') || '-';
    }, [payables]);

    const uniqueCustomers = useMemo(() => {
        return [...new Set(payables.map(p => p.customer).filter(Boolean))].join(', ') || '-';
    }, [payables]);

    const uniqueShippers = useMemo(() => {
        return [...new Set(payables.map(p => p.shipper).filter(Boolean))].join(', ') || '-';
    }, [payables]);

    const handleViewPayable = useCallback(async (payable) => {
        if (payable.type === 'bank_charges') return;
        if (payable.type === 'invoice') {
            // Open the saved KK Tax Invoice when available, otherwise the generated KK invoice PDF
            const invoiceNo = payable.reference_no || payable.job_number || 'Invoice';
            const savedUrl = payable.tax_invoice_url || payable.pdf_url;
            if (savedUrl) {
                previewInvoiceFile(savedUrl, `${payable.tax_invoice_url ? 'KK Tax Invoice' : 'KK Invoice'} - ${invoiceNo}.pdf`, () => setFileNotAvailable(true));
                return;
            }
            if (!payable.job_number) return alert('No job ID for invoice.');
            previewInvoiceFile(`${API_INVOICE_PREVIEW}?job_id=${payable.job_number}`, `Invoice - ${invoiceNo}.pdf`, () => setFileNotAvailable(true));
            return;
        }
        try {
            const apiUrl = payable.type === 'shipping_line' ? API_SHIPPING_LINE_PAYABLE : API_OTHER_PAYABLE;
            const res = await fetch(`${apiUrl}${payable.id}/`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch payable details');
            const data = await res.json();
            setViewPayable(data);
            setViewPayableType(payable.type);
        } catch (err) {
            alert(err.message);
        }
    }, []);

    const toggleExpenseSelection = useCallback((id) => {
        setSelectedExpenseIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const runningTotals = useMemo(() => {
        const totals = { TZS: 0, USD: 0, selectedCount: 0 };
        allExpenses.forEach(p => {
            if (!selectedExpenseIds.has(`${p.type}-${p.id}`)) return;
            totals.selectedCount += 1;
            const currency = p.currency || 'TZS';
            const displayAmount = getPayableDisplayAmount(p, fromInvoicing);
            if (totals[currency] !== undefined) totals[currency] += displayAmount;
        });
        return totals;
    }, [allExpenses, selectedExpenseIds, fromInvoicing]);

    const handleCloseDetail = useCallback(() => {
        setViewPayable(null);
        setViewPayableType(null);
    }, []);

    if (viewPayable && viewPayableType === 'shipping_line') {
        return (
            <div className="dialog-body" style={{ padding: 20, background: 'white', border: '1px solid #ddd', fontFamily: 'Arial, sans-serif', fontSize: '8px' }}>
                <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <h2 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>View Payable</h2>
                    </div>
                    <button className="btn btn-secondary" onClick={handleCloseDetail}>
                        <i className="fa fa-arrow-left" style={{ marginRight: '6px' }}></i> Back to Expenses
                    </button>
                </div>
                <ShippingLinePayablesForm
                    mode="view"
                    initialData={viewPayable}
                    onClose={handleCloseDetail}
                    onSave={handleCloseDetail}
                    showViewExpenses={false}
                    module="SD"
                    popoutMode={true}
                />
            </div>
        );
    }

    if (viewPayable && viewPayableType === 'other') {
        return (
            <div className="dialog-body" style={{ padding: 20, background: 'white', border: '1px solid #ddd', fontFamily: 'Arial, sans-serif', fontSize: '8px' }}>
                <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <h2 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>View Payable</h2>
                    </div>
                    <button className="btn btn-secondary" onClick={handleCloseDetail}>
                        <i className="fa fa-arrow-left" style={{ marginRight: '6px' }}></i> Back to Expenses
                    </button>
                </div>
                <OtherPayablesForm
                    mode="view"
                    initialData={viewPayable}
                    onClose={handleCloseDetail}
                    onSave={handleCloseDetail}
                    showViewExpenses={false}
                    module={viewPayable.module || 'PERMIT'}
                    popoutMode={true}
                />
            </div>
        );
    }

    return (
        <div className="dialog-body" style={{ padding: 20, background: 'white', border: '1px solid #ddd', fontFamily: 'Arial, sans-serif', fontSize: '8px', overflow: 'auto', height: '100vh', boxSizing: 'border-box' }}>
            <style>{`
                .payable-expenses-table tbody tr:hover {
                    background-color: #e9f5ff !important;
                }
            `}</style>
            <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>Expenses</h2>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', background: '#fff3e0', padding: '6px 10px', borderRadius: '4px', border: '1px solid #ffe0b2' }}>
                        <div>
                            <small style={{ color: '#666', fontWeight: 'bold', fontSize: '11px' }}>IDF NUMBER</small>
                            <div style={{ fontWeight: 'bold', color: 'red', fontSize: '12px' }}>{idfNo}</div>
                        </div>
                        <div>
                            <small style={{ color: '#666', fontWeight: 'bold', fontSize: '11px' }}>JOB ID</small>
                            <div style={{ fontWeight: 'bold', color: 'red', fontSize: '12px' }}>{uniqueJobIds}</div>
                        </div>
                        <div>
                            <small style={{ color: '#666', fontWeight: 'bold', fontSize: '11px' }}>BL NUMBER</small>
                            <div style={{ fontWeight: 'bold', color: 'red', fontSize: '12px' }}>{uniqueBlNumbers}</div>
                        </div>
                        <div>
                            <small style={{ color: '#666', fontWeight: 'bold', fontSize: '11px' }}>CUSTOMER</small>
                            <div style={{ fontWeight: 'bold', color: 'red', fontSize: '12px' }}>{uniqueCustomers}</div>
                        </div>
                        <div>
                            <small style={{ color: '#666', fontWeight: 'bold', fontSize: '11px' }}>SHIPPER</small>
                            <div style={{ fontWeight: 'bold', color: 'red', fontSize: '12px' }}>{uniqueShippers}</div>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        <i className="fa fa-times" style={{ marginRight: '6px' }}></i>
                        Close
                    </button>
                </div>
            </div>

            {loading ? <div className="text-center">Loading...</div> : error ? <div className="error">{error}</div> : (
                <>
                    <BankTotals totals={bankTotals} runningTotals={runningTotals} />
                    <SectionTable title="EXPENSES" payables={expenses} extraItems={invoiceCharges} onView={handleViewPayable} selectedIds={selectedExpenseIds} onToggleSelect={toggleExpenseSelection} fromInvoicing={fromInvoicing} />
                    <SectionTable title="PENDING APPROVAL" payables={pendingApproval} onView={handleViewPayable} fromInvoicing={fromInvoicing} />
                </>
            )}
            <ConfirmDialog
                open={fileNotAvailable}
                okOnly
                title="File Not Available"
                message="The file is not available. Please contact the System Administrator."
                onCancel={() => setFileNotAvailable(false)}
            />
        </div>
    );
};

const BankTotals = ({ totals, runningTotals }) => {
    const hasRunningTotals = runningTotals && (runningTotals.TZS > 0 || runningTotals.USD > 0);
    const selectedCount = runningTotals ? runningTotals.selectedCount || 0 : 0;
    if (!totals.length && !hasRunningTotals) return null;
    return (
        <div style={{ marginBottom: 15, padding: 15, background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
                <div>
                    <h4 style={{ margin: '0 0 10px 0', color: '#1565c0', fontSize: '13px' }}>
                        <i className="fa fa-calculator"></i> Bank Account Totals ({totals.length})
                    </h4>
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        {totals.map((t, i) => (
                            <div key={i} style={{ padding: '10px', background: 'white', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minWidth: '200px' }}>
                                <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px' }}>{t.mode}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#1565c0', marginTop: '5px', paddingTop: '5px', borderTop: '1px dashed #ccc' }}>
                                    <span>Total:</span>
                                    <strong>{t.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t.currency}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {hasRunningTotals && (
                    <div style={{ padding: '10px', background: '#fff3e0', borderRadius: '4px', border: '1px solid #ffe0b2', minWidth: '220px' }}>
                        <div style={{ fontWeight: 'bold', borderBottom: '1px solid #ffe0b2', marginBottom: '5px', color: '#e65100' }}>Running Totals ({selectedCount})</div>
                        {runningTotals.USD > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                                <span>USD Total:</span>
                                <strong>{runningTotals.USD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</strong>
                            </div>
                        )}
                        {runningTotals.TZS > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                                <span>TZS Total:</span>
                                <strong>{runningTotals.TZS.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TZS</strong>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};


const SectionTable = ({ title, payables, extraItems = [], onView, selectedIds, onToggleSelect, fromInvoicing }) => {
    const [expandedRows, setExpandedRows] = useState({});

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const sortableValue = useCallback((item, key) => {
        if (key === 'total_amount') {
            return getPayableDisplayAmount(item, fromInvoicing && title === 'EXPENSES');
        }
        if (key === 'payment_date') return item.payment_date ? new Date(item.payment_date).getTime() : 0;
        return item[key];
    }, [fromInvoicing, title]);

    const { items, requestSort, getSortIndicator } = useSortableTable(
        payables,
        { key: title === 'EXPENSES' ? 'payment_date' : 'status', direction: 'ascending' },
        sortableValue
    );

    const hasMultipleCharges = (p) => (p.items || []).length > 1;

    const renderRow = (p) => {
        return (
            <React.Fragment key={`${p.type}-${p.id}`}>
                <tr style={{ background: p.type === 'bank_charges' ? '#f9c9c9' : (expandedRows[`${p.type}-${p.id}`] ? '#fff9c4' : undefined) }}>
                    <td style={{ fontSize: '10px', fontWeight: '600', color: '#6c757d', whiteSpace: 'nowrap' }}>{p.reference_no || '-'}</td>
                    <td style={{ fontSize: '10px' }}>{p.vendor_name}</td>
                    {title === 'EXPENSES' && (
                        <td style={{ fontSize: '10px', textAlign: 'center' }}>
                            <input
                                type="checkbox"
                                checked={selectedIds ? selectedIds.has(`${p.type}-${p.id}`) : false}
                                onChange={() => onToggleSelect && onToggleSelect(`${p.type}-${p.id}`)}
                            />
                        </td>
                    )}
                    <td style={{ fontSize: '10px', textAlign: 'right' }}>
                        {getPayableDisplayAmount(p, fromInvoicing && title === 'EXPENSES').toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ fontSize: '10px' }}>{p.currency}</td>
                    <td style={{ fontSize: '10px', textAlign: 'center' }}>
                        <span style={{
                            padding: '5px 12px',
                            borderRadius: '20px',
                            fontWeight: '500',
                            fontSize: '0.85em',
                            display: 'inline-block',
                            textAlign: 'center',
                            minWidth: '100px',
                            backgroundColor:
                                p.status === 'PENDING' ? '#fff3cd' :
                                p.status === 'APPROVED 1' ? '#cce5ff' :
                                p.status === 'APPROVED 2' ? '#d1ecf1' :
                                p.status === 'APPROVED 3' ? '#d4edda' :
                                p.status === 'CLIENT PAID' ? '#d4edda' :
                                p.status === 'REJECTED' ? '#f8d7da' :
                                '#e2e3e5',
                            color:
                                p.status === 'PENDING' ? '#856404' :
                                p.status === 'APPROVED 1' ? '#004085' :
                                p.status === 'APPROVED 2' ? '#0c5460' :
                                p.status === 'APPROVED 3' ? '#155724' :
                                p.status === 'CLIENT PAID' ? '#155724' :
                                p.status === 'REJECTED' ? '#721c24' :
                                '#383d41',
                            border: '1px solid transparent'
                        }}>
                            {p.status}
                        </span>
                    </td>
                    <td style={{ fontSize: '10px' }}>{formatDateDisplay(p.payment_date)}</td>
                    <td style={{ fontSize: '10px' }}>{p.mode}</td>
                    <td style={{ fontSize: '10px' }}>{p.cheque_no || '-'}</td>
                    <td style={{ fontSize: '10px', maxWidth: '260px' }}>
                        {hasMultipleCharges(p) ? (
                            <span
                                style={{ cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                onClick={() => toggleRow(`${p.type}-${p.id}`)}
                                title="Click to expand charge details"
                            >
                                <i className={`fa fa-chevron-${expandedRows[`${p.type}-${p.id}`] ? 'down' : 'right'}`} style={{ color: '#555' }}></i>
                            </span>
                        ) : (
                            p.charge_names || '-'
                        )}
                    </td>
                    {title === 'EXPENSES' && <td style={{ fontSize: '10px' }}>{p.paid_by || '-'}</td>}
                    <td style={{ fontSize: '10px' }}>{p.created_by}</td>
                    <td style={{ fontSize: '10px' }}>{p.approved_by}</td>
                    <td style={{ fontSize: '10px' }}>
                        {p.type !== 'bank_charges' && (
                            <button className="btn-icon" onClick={() => onView(p)} title="View">
                                <i className="fa fa-eye"></i>
                            </button>
                        )}
                    </td>
                </tr>
                {expandedRows[`${p.type}-${p.id}`] && hasMultipleCharges(p) && (
                    <tr>
                        <td colSpan={title === 'EXPENSES' ? 14 : 12} style={{ padding: 0, borderBottom: '2px solid #dee2e6' }}>
                            <div style={{ padding: 12, background: '#f8f9fa' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#333' }}>
                                    <i className="fa fa-table" style={{ marginRight: 5 }}></i>
                                    Charge Details
                                </div>
                                <table className="table table-bordered table-sm" style={{ fontSize: '8px', marginBottom: 0, width: 'auto' }}>
                                    <thead style={{ background: '#e8f5e9' }}>
                                        <tr>
                                            <th colSpan={2}>Charge Name</th>
                                            <th style={{ textAlign: 'right' }}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(p.items || []).length > 0 ? (
                                            p.items.map((item, idx) => {
                                                const displayItemAmount = getChargeDisplayAmount(item, p);
                                                return (
                                                    <tr key={idx}>
                                                        <td colSpan={2}>{item.charge_name}</td>
                                                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{displayItemAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {p.currency}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={3} className="text-center text-muted">No charge items found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    return (
        <div style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 10, color: '#333', fontSize: '11px', fontWeight: '600' }}>{title}</h3>
            <table className="list-table payable-expenses-table" style={{ fontSize: '10px' }}>
                <thead>
                    <tr>
                        <th onClick={() => requestSort('reference_no')} style={{ cursor: 'pointer', fontSize: '11px', fontWeight: 'normal' }}>Ref No {getSortIndicator('reference_no')}</th>
                        <th onClick={() => requestSort('vendor_name')} style={{ cursor: 'pointer', fontSize: '11px', fontWeight: 'normal' }}>Vendor {getSortIndicator('vendor_name')}</th>
                        {title === 'EXPENSES' && <th style={{ fontSize: '11px', fontWeight: 'normal', textAlign: 'center' }}>Select</th>}
                        <th onClick={() => requestSort('total_amount')} style={{ cursor: 'pointer', fontSize: '11px', fontWeight: 'normal' }}>Total Amount {getSortIndicator('total_amount')}</th>
                        <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Currency</th>
                        <th onClick={() => requestSort('status')} style={{ cursor: 'pointer', fontSize: '11px', fontWeight: 'normal', textAlign: 'center' }}>Status {getSortIndicator('status')}</th>
                        <th onClick={() => requestSort('payment_date')} style={{ cursor: 'pointer', fontSize: '11px', fontWeight: 'normal' }}>Payment Date {getSortIndicator('payment_date')}</th>
                        <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Mode</th>
                        <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Chq No</th>
                        <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Charges</th>
                        {title === 'EXPENSES' && <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Paid By</th>}
                        <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Created By</th>
                        <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Approved By</th>
                        <th style={{ fontSize: '11px', fontWeight: 'normal' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(renderRow)}
                    {(extraItems || []).map(renderRow)}
                    {items.length === 0 && (extraItems || []).length === 0 && (
                        <tr><td colSpan={title === 'EXPENSES' ? 14 : 12} className="text-center">No records found</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default PayableExpensesView;
